import { useEffect } from "react";
import { eventMetadata } from "./seo/eventMetadata";
import { pageForPath, canonicalUrl, metaTags, structuredData } from "./seo/landingMetadata";
import { blogMetadata } from "./seo/blogMetadata";

const setMeta = (attr, name, content) => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
  return el;
};

export const useEventPageMeta = (kind, event, content, eventId) => {
  useEffect(() => {
    const metadata = eventMetadata(kind, event, content, eventId);
    document.title = metadata.title;
    document.getElementById("landing-page-schema")?.remove();
    document.getElementById("blog-page-schema")?.remove();
    const owned = metadata.tags.map(([attr, name, value]) => setMeta(attr, name, value));
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = metadata.canonical;
    const schema = document.createElement("script"); schema.id = "event-page-schema"; schema.type = "application/ld+json";
    if (metadata.schema) { schema.textContent = JSON.stringify(metadata.schema); document.head.appendChild(schema); }
    return () => { owned.forEach(el => el.remove()); canonical.remove(); schema.remove(); };
  }, [kind, event, content, eventId]);
};

export const useBlogMeta = (post = null, status = 200) => {
  useEffect(() => {
    const metadata = blogMetadata(post, status);
    document.title = metadata.title;
    document.getElementById("landing-page-schema")?.remove();
    document.getElementById("blog-page-schema")?.remove();
    const owned = metadata.tags.map(([attr, name, value]) => setMeta(attr, name, value));
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = metadata.canonical;
    const schema = document.createElement("script"); schema.id = "blog-page-schema"; schema.type = "application/ld+json";
    if (metadata.schema) { schema.textContent = JSON.stringify(metadata.schema); document.head.appendChild(schema); }
    return () => { owned.forEach((element) => element.remove()); canonical.remove(); schema.remove(); };
  }, [post, status]);
};

export const useLandingPageMeta = (pathname, overrides = {}) => {
  const headline = overrides.headline;
  const description = overrides.description;
  useEffect(() => {
    const original = pageForPath(pathname);
    if (!original) return undefined;
    const page = { ...original };
    if (headline) { page.headline = headline; page.title = `${headline} | Nonprofit Board Builder`; }
    if (description) page.description = description;
    document.title = page.title;
    const owned = metaTags(page).map(([attr, key, value]) => setMeta(attr, key, value));
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = canonicalUrl(page);
    let schema = document.getElementById("landing-page-schema");
    if (!schema) { schema = document.createElement("script"); schema.id = "landing-page-schema"; schema.type = "application/ld+json"; document.head.appendChild(schema); }
    schema.textContent = JSON.stringify(structuredData(page));
    return () => {
      owned.forEach((element) => element.remove());
      canonical.remove(); schema.remove();
    };
  }, [pathname, headline, description]);
};

export const usePageMeta = (title, description, noindex = false) => {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (noindex) {
      setMeta("name", "robots", "noindex");
      return () => {
        const el = document.head.querySelector('meta[name="robots"]');
        if (el) el.remove();
      };
    }
    return undefined;
  }, [title, description, noindex]);
};

export const PAGE_META = {
  home: ["Nonprofit Board Builder | Build the Board Your Mission Deserves", "Build a stronger nonprofit board with committed people who can help raise money, strengthen your organization and move your mission forward. Recruit, reactivate and activate your board with Nonprofit Board Builder."],
  recruitment: ["Recruit Nonprofit Board Members | Nonprofit Board Builder", "Start recruiting committed, capable board members with the skills, experience, relationships and fundraising capacity your nonprofit needs to move forward."],
  process: ["Build the Board Your Nonprofit Needs | Nonprofit Board Builder", "See the five steps for building a committed nonprofit board that strengthens your organization, supports fundraising and helps move your mission forward."],
  checkout: ["Guided Board Recruitment | Nonprofit Board Builder", "Get the guidance, resources and support you need to recruit committed, capable board members and build the stronger board your nonprofit needs."],
  recruitWithRooney: ["Recruit With Rooney | Nonprofit Board Builder", "Recruit the board members your nonprofit needs with direct guidance and support from Rooney Akpesiri."],
  boardRecruitmentProposal: ["Board Recruitment Project Proposal | Nonprofit Board Builder", "Work directly with Rooney to recruit and onboard the skilled board members your nonprofit needs."],
  aboutRooney: ["Rooney Akpesiri | The Nonprofit Board Builder", "Helping nonprofits build strong fundraising board. Discover the two ways Rooney can help you recruit the board your nonprofit needs."],
  recruitYourBoardYourself: ["Recruit Your Board Yourself | Nonprofit Board Builder", "Follow Rooney's board recruitment process, use the execution materials provided and recruit the board members your nonprofit needs."],
  boardRecruitmentIntake: ["Tell Me About Your Organization and Board | Nonprofit Board Builder", "Provide the information needed to begin your board recruitment engagement."],
  reactivation: ["Reactivate Your Nonprofit Board | Nonprofit Board Builder", "Help committed board members step up, give inactive members a respectful path to step down and rebuild an engaged board ready to move your nonprofit forward."],
  reactivateWithRooney: ["Reactivate Your Board With Rooney | Nonprofit Board Builder", "Reactivate your board without destroying relationships. Find out who is ready to stand up and give them clear responsibility."],
  reactivateYourBoardYourself: ["Reactivate Your Board Yourself | Nonprofit Board Builder", "Follow the Board Reactivation process, have the conversations that need to happen and give the people who stay clear responsibility."],
  boardReactivationProposal: ["Board Reactivation Project Proposal | Nonprofit Board Builder", "Work directly with Rooney to reactivate the people who are ready to serve and deal with the people who aren't."],
  boardReactivationIntake: ["Tell Me About Your Board | Nonprofit Board Builder", "Provide the information needed to begin your board reactivation engagement."],
  fundraising_activation: ["Activate Your Board to Raise Money | Nonprofit Board Builder", "Activate your nonprofit board around fundraising so members use their strengths, relationships and experience to help raise money and strengthen your organization."],
  activateWithRooney: ["Activate Your Board With Rooney | Nonprofit Board Builder", "Turn your board into fundraising champions for your mission. Get your board involved in building the fundraising plan and taking ownership of execution."],
  activateYourBoardYourself: ["Activate Your Board Yourself | Nonprofit Board Builder", "Lead your board through the complete fundraising activation process yourself, with the process, tools and support you need."],
  boardActivationProposal: ["Board Fundraising Activation Project Proposal | Nonprofit Board Builder", "Work directly with Rooney to get your board involved in building the fundraising plan, adopting the strategy and helping raise money."],
  boardActivationIntake: ["Tell Me About Your Fundraising | Nonprofit Board Builder", "Provide the information needed to begin your board fundraising activation engagement."],
  blog: ["Nonprofit Board Recruitment, Reactivation & Fundraising | Nonprofit Board Builder", "Practical insights for nonprofit founders and executive directors who want to recruit stronger board members, reactivate their present board and get their board involved in fundraising."],
};
